import { createContext, useContext, useEffect, useState } from 'react'

/**
 * Context for sharing online/offline status across all components
 */
const OnlineStatusContext = createContext<boolean>(true)

/**
 * Provider component that manages online/offline status globally
 */
export function OnlineStatusProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isOnline, setIsOnline] = useState(true)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    // Only update state when we actually detect an online/offline event
    // Don't rely on initial navigator.onLine state as it's unreliable
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return (
    <OnlineStatusContext.Provider value={isOnline}>
      {children}
    </OnlineStatusContext.Provider>
  )
}

/**
 * Hook to access the shared online/offline status
 * @returns boolean indicating whether the browser is currently online
 */
export function useOnlineStatus() {
  return useContext(OnlineStatusContext)
}
